// src/modules/beat/BeatList.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBeats, deleteBeat, Beat } from "./beatApi";
import styles from "./BeatShared.module.css";
import {
  Music,
  Plus,
  Edit,
  Trash2,
  Search,
  Clock,
  Volume2,
  Zap,
  BarChart3,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

export default function BeatList() {
  const [beats, setBeats] = useState<Beat[]>([]);
  const [filteredBeats, setFilteredBeats] = useState<Beat[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadBeats();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredBeats(beats);
    } else {
      const filtered = beats.filter(
        (beat) =>
          beat.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          beat.rhythm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          beat.genre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBeats(filtered);
    }
  }, [searchTerm, beats]);

  const loadBeats = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchBeats();
      console.log("Beats loaded:", response);
      setBeats(response || []);
    } catch (error: any) {
      console.error("Failed to load beats:", error);
      setError(
        error.response?.data?.message || "Beat'ler yüklenirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (
      window.confirm(`"${title}" beat'ini silmek istediğinize emin misiniz?`)
    ) {
      try {
        await deleteBeat(id);
        setBeats(beats.filter((beat) => beat.beat_id !== id));
        console.log(`Beat deleted: ${title}`);
      } catch (error: any) {
        console.error("Delete failed:", error);
        alert("Beat silinirken hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTotalDuration = () => {
    return filteredBeats.reduce(
      (total, beat) => total + (beat.duration_seconds || 0),
      0
    );
  };

  const getGenreStats = () => {
    const genres: { [key: string]: number } = {};
    filteredBeats.forEach((beat) => {
      if (beat.genre) {
        genres[beat.genre] = (genres[beat.genre] || 0) + 1;
      }
    });
    return Object.entries(genres).slice(0, 3);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>Beat'ler yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody} style={{ textAlign: "center" }}>
            <AlertCircle
              size={48}
              style={{ color: "#ef4444", marginBottom: "1rem" }}
            />
            <h3 style={{ color: "white", marginBottom: "0.5rem" }}>
              Hata Oluştu
            </h3>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.8)",
                marginBottom: "1.5rem",
              }}
            >
              {error}
            </p>
            <button onClick={loadBeats} className={styles.button}>
              <RefreshCw size={16} />
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Music className={styles.titleIcon} />
            Beat Yönetimi
          </h1>
          <p className={styles.subtitle}>
            Ritim koleksiyonunuzu yönetin ve düzenleyin
          </p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/beats/add" className={styles.primaryButton}>
            <Plus size={16} />
            Yeni Beat Ekle
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchSection}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder="Beat veya tür ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#3b82f6" }}>
            <Music size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{filteredBeats.length}</h3>
            <p>Toplam Beat</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#10b981" }}>
            <Clock size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{formatDuration(getTotalDuration())}</h3>
            <p>Toplam Süre</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#8b5cf6" }}>
            <BarChart3 size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{getGenreStats().length}</h3>
            <p>Farklı Tür</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#f59e0b" }}>
            <Zap size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>
              {filteredBeats.length > 0
                ? Math.round(
                    filteredBeats.reduce(
                      (sum, beat) => sum + (beat.bpm || 0),
                      0
                    ) / filteredBeats.length
                  )
                : 0}
            </h3>
            <p>Ortalama BPM</p>
          </div>
        </div>
      </div>

      {/* Beats List */}
      {filteredBeats.length === 0 ? (
        <div className={styles.card}>
          <div className={styles.emptyState}>
            <Music size={48} style={{ opacity: 0.3 }} />
            <p>
              {searchTerm
                ? "Arama kriterlerine uygun beat bulunamadı"
                : "Henüz beat eklenmemiş"}
            </p>
            {!searchTerm && (
              <Link to="/beats/add" className={styles.emptyStateButton}>
                <Plus size={16} />
                İlk Beat'i Ekle
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.contentGrid}>
          {filteredBeats.map((beat) => (
            <div key={beat.beat_id} className={styles.card}>
              <div className={styles.cardBody}>
                <div className={styles.songHeader}>
                  <div className={styles.songInfo}>
                    <h3 className={styles.songTitle}>{beat.title}</h3>
                    <p className={styles.songArtist}>
                      <Volume2 size={14} />
                      {beat.rhythm}
                    </p>
                  </div>
                  <div className={styles.songActions}>
                    <Link
                      to={`/beats/edit/${beat.beat_id}`}
                      className={styles.actionButton}
                      style={{ background: "#3b82f6" }}
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </Link>
                    <button
                      onClick={() => handleDelete(beat.beat_id, beat.title)}
                      className={styles.actionButton}
                      style={{ background: "#ef4444" }}
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.songMeta}>
                  <div className={styles.metaItem}>
                    <Clock size={14} />
                    <span>{formatDuration(beat.duration_seconds || 0)}</span>
                  </div>

                  {beat.bpm && (
                    <div className={styles.metaItem}>
                      <Zap size={14} />
                      <span>{beat.bpm} BPM</span>
                    </div>
                  )}

                  {beat.genre && (
                    <div className={styles.metaItem}>
                      <Music size={14} />
                      <span>{beat.genre}</span>
                    </div>
                  )}
                </div>

                <div className={styles.songFooter}>
                  <span className={styles.songId}>
                    #{beat.beat_id.substring(0, 8)}...
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
